import React, { useState, useEffect } from 'react';
import { Bot, AlertCircle, Zap, RefreshCw, Sliders, Settings, PlusCircle, Copy, Loader, X, Activity } from 'lucide-react';
import { Token } from '../../types/supabase';
import { useWalletUser } from '../../hooks/useWalletUser';
import { getUserProfile } from '../../services/userProfileService';
import AutoTraderPositions from '../../components/AutoTraderPositions';
import { globalWidgetTextStyles, widgetClassName } from '../../styles/globalWidgetStyles';

// Define trading strategy types
interface TradingStrategy {
  id: string;
  name: string;
  description: string;
  risk: 'low' | 'medium' | 'high';
  type: 'momentum' | 'reversal' | 'breakout' | 'custom';
  isActive: boolean;
}

// Auto Trader component props
interface AutoTraderProps {
  selectedToken?: Token;
  onClose?: () => void;
  isWidget?: boolean;
}

const AutoTrader: React.FC<AutoTraderProps> = ({ selectedToken, onClose, isWidget = false }) => {
  const walletUser = useWalletUser();
  
  // Component state
  const [strategies, setStrategies] = useState<TradingStrategy[]>([]);
  const [selectedStrategy, setSelectedStrategy] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | React.ReactNode | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTradingWallet, setActiveTradingWallet] = useState<string | null>(null);
  const [tradeAmount, setTradeAmount] = useState<string>('0.1');
  const [maxDailyTrades, setMaxDailyTrades] = useState<number>(10);
  const [isCreatingStrategy, setIsCreatingStrategy] = useState<boolean>(false);
  const [copied, setCopied] = useState(false);

  // Add a modal state for creating a new strategy
  const [newStrategy, setNewStrategy] = useState({
    name: '',
    description: '',
    risk: 'medium' as 'low' | 'medium' | 'high',
    type: 'momentum' as 'momentum' | 'reversal' | 'breakout' | 'custom'
  });

  // Add state to control tab view
  const [activeTab, setActiveTab] = useState<'settings' | 'positions'>('settings');

  // Mock strategies for demo
  const mockStrategies: TradingStrategy[] = [
    {
      id: '1',
      name: 'Momentum Trader',
      description: 'Buy tokens with strong upward momentum and volatility',
      risk: 'medium',
      type: 'momentum',
      isActive: false
    },
    {
      id: '2',
      name: 'Reversal Hunter',
      description: 'Look for price reversals after significant drops',
      risk: 'high',
      type: 'reversal',
      isActive: false
    },
    {
      id: '3',
      name: 'Breakout Detector',
      description: 'Identify tokens breaking through resistance levels',
      risk: 'medium',
      type: 'breakout',
      isActive: false
    }
  ];

  // Get active trading wallet when component loads
  useEffect(() => {
    const fetchActiveTradingWallet = async () => {
      if (walletUser.walletAddress) {
        try {
          const profile = await getUserProfile(walletUser.walletAddress);
          if (profile?.active_trade_wallet) {
            setActiveTradingWallet(profile.active_trade_wallet);
          } else {
            setError('Please activate a trading wallet to use the Auto Trader');
          }
        } catch (err) {
          console.error('Error fetching active trading wallet:', err);
        }
      }
    };

    fetchActiveTradingWallet();
    
    // Populate strategies
    setStrategies(mockStrategies);
    
    // Listen for active wallet changes
    const handleActiveWalletChanged = (event: CustomEvent<{previousWallet: string | null, currentWallet: string | null}>) => {
      const { currentWallet } = event.detail;
      
      setActiveTradingWallet(currentWallet);
      
      if (!currentWallet) {
        setError('Please activate a trading wallet to use the Auto Trader');
        // Deactivate all strategies if wallet is deactivated
        setStrategies(prev => prev.map(s => ({ ...s, isActive: false })));
      } else {
        if (error === 'Please activate a trading wallet to use the Auto Trader' || 
            error === 'Please activate a trading wallet in your wallet dashboard first') {
          setError(null);
        }
      }
    };
    
    window.addEventListener('ekko:active_wallet_changed', handleActiveWalletChanged as EventListener);
    
    return () => {
      window.removeEventListener('ekko:active_wallet_changed', handleActiveWalletChanged as EventListener);
    };
  }, [walletUser.walletAddress, error]);

  const toggleStrategy = (strategyId: string) => {
    if (!activeTradingWallet) {
      setError('Please activate a trading wallet in your wallet dashboard first');
      return;
    }

    setIsLoading(true);
    setError(null);

    setTimeout(() => {
      setStrategies(prev => 
        prev.map(strategy => 
          strategy.id === strategyId 
            ? { ...strategy, isActive: !strategy.isActive } 
            : strategy
        )
      );
      
      const updatedStrategy = strategies.find(s => s.id === strategyId);
      const newState = !updatedStrategy?.isActive;
      
      setSuccess(`${updatedStrategy?.name} ${newState ? 'activated' : 'deactivated'} successfully!`);
      setTimeout(() => setSuccess(null), 3000);
      
      setIsLoading(false);
    }, 1000);
  };

  const handleAmountSelect = (selectedAmount: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setTradeAmount(selectedAmount);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setSuccess("Copied to clipboard!");
        setTimeout(() => setSuccess(null), 2000);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  const getRiskColor = (risk: 'low' | 'medium' | 'high') => {
    switch (risk) {
      case 'low': return 'bg-green-500/20 text-green-400';
      case 'medium': return 'bg-yellow-500/20 text-yellow-400';
      case 'high': return 'bg-red-500/20 text-red-400';
      default: return 'bg-blue-500/20 text-blue-400';
    }
  };

  const customInputStyles = `
    .glass-input {
      background-color: rgba(12, 20, 26, 0.26) !important;
      backdrop-filter: blur(6px);
      -webkit-backdrop-filter: blur(6px);
      border: 1px solid rgba(255, 255, 255, 0.1);
    }
    
    .glass-input:focus {
      border-color: #47be8f;
      outline: none;
      box-shadow: 0 0 0 1px rgba(12, 20, 26, 0.26);
    }
  `;

  // Handle closing the create strategy modal
  const handleCloseStrategyModal = () => {
    setIsCreatingStrategy(false);
  };

  // Handle creating a new strategy
  const handleCreateStrategy = () => {
    if (!newStrategy.name || !newStrategy.description) {
      setError('Please provide a name and description for your strategy');
      return;
    }

    const id = `strategy-${Date.now()}`;
    const strategy: TradingStrategy = {
      ...newStrategy,
      id,
      isActive: false
    };

    setStrategies(prev => [...prev, strategy]);
    setSuccess(`Strategy "${newStrategy.name}" created successfully!`);
    setTimeout(() => setSuccess(null), 3000);
    
    // Reset the form and close modal
    setNewStrategy({
      name: '',
      description: '',
      risk: 'medium',
      type: 'momentum'
    });
    setIsCreatingStrategy(false);
  };

  // Strategy creation modal
  const renderStrategyModal = () => {
    if (!isCreatingStrategy) return null;
    
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4 w-full max-w-md shadow-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-black font-medium">Create New Strategy</h3>
            <button 
              onClick={handleCloseStrategyModal}
              className="text-gray-400 hover:text-gray-600"
            >
              &times;
            </button>
          </div>
          
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Name</label>
              <input
                type="text"
                value={newStrategy.name}
                onChange={(e) => setNewStrategy({...newStrategy, name: e.target.value})}
                className="w-full h-9 rounded px-3 py-1 text-black text-sm border border-gray-200 focus:border-[#24c35b] focus:ring-1 focus:ring-[#24c35b]"
                placeholder="Strategy name"
              />
            </div>
            
            <div>
              <label className="block text-xs text-gray-600 mb-1">Description</label>
              <textarea
                value={newStrategy.description}
                onChange={(e) => setNewStrategy({...newStrategy, description: e.target.value})}
                className="w-full h-20 rounded px-3 py-1 text-black text-sm border border-gray-200 focus:border-[#24c35b] focus:ring-1 focus:ring-[#24c35b]"
                placeholder="Describe your strategy..."
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Risk Level</label>
                <select
                  value={newStrategy.risk}
                  onChange={(e) => setNewStrategy({...newStrategy, risk: e.target.value as 'low' | 'medium' | 'high'})}
                  className="w-full h-9 rounded px-3 py-1 text-black text-sm border border-gray-200 focus:border-[#24c35b] focus:ring-1 focus:ring-[#24c35b]"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              
              <div>
                <label className="block text-xs text-gray-600 mb-1">Strategy Type</label>
                <select
                  value={newStrategy.type}
                  onChange={(e) => setNewStrategy({...newStrategy, type: e.target.value as 'momentum' | 'reversal' | 'breakout' | 'custom'})}
                  className="w-full h-9 rounded px-3 py-1 text-black text-sm border border-gray-200 focus:border-[#24c35b] focus:ring-1 focus:ring-[#24c35b]"
                >
                  <option value="momentum">Momentum</option>
                  <option value="reversal">Reversal</option>
                  <option value="breakout">Breakout</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
            </div>
            
            <div className="pt-3">
              <button
                onClick={handleCreateStrategy}
                className="w-full h-9 rounded bg-[#24c35b] hover:bg-[#1eaa4e] text-white font-medium transition-colors"
              >
                Create Strategy
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isWidget) {
    return (
      <>
        <div className="absolute inset-0 m-0 p-0 overflow-hidden">
          <div className={`w-full h-full flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm relative ${widgetClassName}`} data-widget-type="auto-trader">
            <style>{customInputStyles}</style>
            <style>{globalWidgetTextStyles}</style>
            
            <div className="p-3 border-b border-gray-200 bg-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#24c35b]"></div>
                <Bot size={18} className="text-[#24c35b]" />
                <h2 className="text-lg font-bold text-black">Auto Trader</h2>
              </div>
              {onClose && (
                <button 
                  onClick={onClose}
                  className="text-gray-500 hover:text-black transition-colors p-1 rounded hover:bg-gray-100"
                  title="Close"
                >
                  <X size={18} />
                </button>
              )}
            </div>
            
            <div className="flex-1 overflow-y-auto p-3">
              {selectedToken?.image_uri && (
                <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none z-0">
                  <div 
                    className="w-full h-full"
                    style={{
                      background: `url(${selectedToken.image_uri})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      opacity: 0.1,
                      filter: 'blur(1px)',
                      maskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)',
                      WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)'
                    }}
                  />
                </div>
              )}
              
              {selectedToken && (
                <div className="border-b border-gray-200 bg-gray-50 relative z-10 mb-3 p-2 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2">
                        {selectedToken.image_uri && (
                          <img 
                            src={selectedToken.image_uri} 
                            alt={selectedToken.symbol || 'Token'} 
                            className="w-8 h-8 rounded-md object-cover border border-gray-200"
                          />
                        )}
                        <span className="font-medium text-black">{selectedToken.symbol}</span>
                        <span className="text-xs text-gray-600">({selectedToken.name})</span>
                        <button
                          onClick={() => {
                            copyToClipboard(selectedToken.mint || '');
                            setCopied(true);
                            setTimeout(() => setCopied(false), 1200);
                          }}
                          className={`ml-2 transition-colors ${copied ? 'text-[#24c35b]' : 'text-gray-400 hover:text-[#24c35b]'}`}
                          title={copied ? 'Copied!' : 'Copy mint address'}
                        >
                          <Copy size={12} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex border-b border-gray-200 bg-gray-50 mb-3 -mx-3 px-3">
                <button
                  onClick={() => setActiveTab('settings')}
                  className={`py-3 px-4 text-sm font-medium border-b-2 ${
                    activeTab === 'settings' 
                      ? 'text-[#24c35b] border-[#24c35b] bg-white' 
                      : 'text-gray-600 border-transparent hover:text-black hover:bg-gray-100'
                  }`}
                >
                  <Settings size={16} className="mr-2 inline" />
                  Settings
                </button>
                <button
                  onClick={() => setActiveTab('positions')}
                  className={`py-3 px-4 text-sm font-medium border-b-2 ${
                    activeTab === 'positions' 
                      ? 'text-[#24c35b] border-[#24c35b] bg-white' 
                      : 'text-gray-600 border-transparent hover:text-black hover:bg-gray-100'
                  }`}
                >
                  <Activity size={16} className="mr-2 inline" />
                  Positions
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto">
                {activeTab === 'settings' ? (
                  <>
                    <div className="mb-4">
                      <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                        <Settings size={14} className="mr-1" />
                        Trading Settings
                      </h3>
                      
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Trade Amount (SOL)</label>
                          <div className="grid grid-cols-2 gap-1">
                            <button
                              type="button"
                              onClick={(e) => handleAmountSelect('0.1', e)}
                              className={`h-8 rounded text-xs px-2 transition-colors ${tradeAmount === '0.1' ? 'bg-[#24c35b] text-white font-medium' : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'}`}
                            >
                              0.1
                            </button>
                            <button
                              type="button"
                              onClick={(e) => handleAmountSelect('1', e)}
                              className={`h-8 rounded text-xs px-2 transition-colors ${tradeAmount === '1' ? 'bg-[#24c35b] text-white font-medium' : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'}`}
                            >
                              1
                            </button>
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Max Daily Trades</label>
                          <input
                            type="text"
                            value={maxDailyTrades}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d+$/.test(value)) {
                                const numValue = parseInt(value) || 0;
                                if (numValue >= 1 && numValue <= 100) {
                                  setMaxDailyTrades(numValue);
                                } else if (value === '') {
                                  setMaxDailyTrades(0);
                                }
                              }
                            }}
                            className="w-full h-8 rounded px-3 py-1 text-black text-sm border border-gray-200 focus:border-[#24c35b] focus:ring-1 focus:ring-[#24c35b]"
                            placeholder="1-100"
                            inputMode="numeric"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex-1 overflow-y-auto mb-3">
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="text-sm font-medium text-gray-700 flex items-center">
                          <Bot size={14} className="mr-1" />
                          Trading Strategies
                        </h3>
                        <button 
                          className="text-xs flex items-center gap-1 text-[#24c35b] hover:text-[#1eaa4e] transition-colors"
                          onClick={() => setIsCreatingStrategy(true)}
                        >
                          <PlusCircle size={12} />
                          New
                        </button>
                      </div>
                      
                      <div className="space-y-2">
                        {strategies.length === 0 ? (
                          <div className="text-center py-4 text-gray-500 text-sm">
                            No strategies available
                          </div>
                        ) : (
                          strategies.map(strategy => (
                            <div 
                              key={strategy.id} 
                              className={`p-3 rounded border ${strategy.isActive 
                                ? 'border-[#24c35b]/40 bg-[#24c35b]/10' 
                                : 'border-gray-200 bg-gray-50 hover:bg-gray-100'} 
                                transition-all cursor-pointer relative`}
                              onClick={() => setSelectedStrategy(strategy.id)}
                            >
                              <div className="flex justify-between items-start">
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-black">{strategy.name}</span>
                                    <span className={`text-xs px-2 rounded-full ${getRiskColor(strategy.risk)}`}>
                                      {strategy.risk}
                                    </span>
                                  </div>
                                  <p className="text-xs text-gray-600 mt-1">{strategy.description}</p>
                                </div>
                                
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleStrategy(strategy.id);
                                  }}
                                  disabled={isLoading}
                                  className={`px-3 py-1 text-xs rounded ${
                                    strategy.isActive 
                                      ? 'bg-red-500 hover:bg-red-600 text-white' 
                                      : 'bg-[#24c35b] hover:bg-[#1eaa4e] text-white'
                                  } transition-colors font-medium`}
                                >
                                  {isLoading ? <Loader size={12} className="animate-spin" /> : (strategy.isActive ? 'Stop' : 'Start')}
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <AutoTraderPositions />
                )}
              </div>
              
              <div className="mt-2 w-full">
                {error && (
                  <div className="p-1.5 mb-1.5 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
                    <AlertCircle size={12} className="text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-red-700">{error}</p>
                  </div>
                )}
                
                {success && (
                  <div className="p-1.5 mb-1.5 bg-green-50 border border-green-200 rounded-md flex items-start gap-2">
                    <Zap size={12} className="text-green-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-green-700">{success}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {renderStrategyModal()}
      </>
    );
  }

  return (
    <>
      <div className={`p-4 bg-white border border-gray-200 rounded-lg shadow-sm ${widgetClassName}`}>
        <div className="p-2 border-b border-gray-200 bg-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-[#24c35b] rounded-full"></div>
            <Bot size={18} className="text-[#24c35b]" />
            <h2 className="text-lg font-bold text-black">Auto Trader</h2>
          </div>
          {onClose && (
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded hover:bg-gray-50"
              title="Close"
            >
              <X size={16} />
            </button>
          )}
        </div>
        {/* Modal content would go here */}
      </div>
      {renderStrategyModal()}
    </>
  );
};

export const createAutoTraderWidget = () => {
  return {
    type: 'auto-trader',
    title: 'Auto Trader',
    content: <AutoTrader isWidget={true} />,
    size: { width: 640, height: 640 },
    padding: 0,
    margin: 0,
    contentStyle: { 
      padding: 0, 
      margin: 0,
      backgroundColor: 'transparent',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflow: 'hidden'
    },
    disablePadding: true,
    fullWidth: true,
    fullHeight: true,
    background: 'transparent',
    glassMorphism: true,
    containerClassName: 'p-0 m-0 overflow-hidden',
    containerStyle: {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(12px)',
      borderRadius: '12px',
      border: '1px solid rgba(36, 195, 91, 0.2)',
      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(36, 195, 91, 0.2)',
      overflow: 'hidden',
      padding: '10px',
      margin: 0
    }
  };
};

export default AutoTrader;
